import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, HeadlineS } from '@sorare/core/src/atoms/typography';
import { Card } from '@sorare/core/src/components/card/Card';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useToggle } from '@sorare/core/src/hooks/useToggle';

import { Team } from '#src/components/Team/index.tsx';
import { InSeasonWarning } from '#src/components/buyActions/InSeasonWarning/index.tsx';
import { TokenDrawerSummary } from '#src/components/buyActions/TokenDrawerSummary/index.tsx';
import { AnyCardPriceHistory } from '#src/components/price/AnyCardPriceHistory/index.tsx';
import { CardDescription } from '#src/components/token/CardDescription/index.tsx';

import type { TokenSummary_anyCard } from './__generated__/index.graphql.ts';

const Image = styled.div<{ small: boolean }>`
  flex-shrink: 0;
  width: 52px;
  ${({ small }) => (small ? 'width: 30px;' : '')}
`;

const Metas = styled.div`
  flex-grow: 1;
`;

type Props = {
  card: TokenSummary_anyCard;
  title?: ReactNode;
  price?: ReactNode;
  withoutRecentSales?: boolean;
  small?: boolean;
};

export const TokenSummary = ({
  card,
  title,
  price,
  withoutRecentSales = false,
  small = false,
}: Props) => {
  const [viewRecentSales, toggleViewRecentSales] = useToggle(false);

  const { up: isLaptop } = useScreenSize('laptop');

  return (
    <div>
      <Vertical gap={2}>
        {title}
        <Horizontal gap={1.5}>
          <Image small={small}>
            <Card card={card} />
          </Image>
          <Metas>
            <CardDescription
              withoutLink
              Title={BodyM}
              titleColor="var(--c-white)"
              detailsColor="var(--c-nd-600)"
              Details={BodyS}
              card={card}
            />
            <Team card={card} />
            {!withoutRecentSales && card.rarityTyped !== Rarity.unique && (
              <button type="button" onClick={toggleViewRecentSales}>
                <BodyS color="var(--c-blue-400)">
                  {isLaptop && viewRecentSales ? (
                    <FormattedMessage defaultMessage="Hide recent sales" />
                  ) : (
                    <FormattedMessage defaultMessage="View recent sales" />
                  )}
                </BodyS>
              </button>
            )}
          </Metas>
          {price}
        </Horizontal>
        <InSeasonWarning card={card} />
      </Vertical>
      {!isLaptop ? (
        <TokenDrawerSummary
          open={viewRecentSales}
          onBackdropClick={toggleViewRecentSales}
          title={
            <HeadlineS as="h5">
              <FormattedMessage defaultMessage="Recent Sales" />
            </HeadlineS>
          }
        >
          <AnyCardPriceHistory context="buy_modal" card={card} />
        </TokenDrawerSummary>
      ) : (
        viewRecentSales && (
          <Collapse open={viewRecentSales}>
            <AnyCardPriceHistory context="buy_modal" card={card} />
          </Collapse>
        )
      )}
    </div>
  );
};

TokenSummary.fragments = {
  anyCard: gql`
    fragment TokenSummary_anyCard on AnyCardInterface {
      slug
      rarityTyped
      ...Card_anyCard
      ...CardDescription_anyCard
      ...Team_anyCard
      ...InSeasonWarning_anyCard
      ...AnyCardPriceHistory_anyCard
    }
    ${AnyCardPriceHistory.fragments.anyCard}
    ${Card.fragments.anyCard}
    ${CardDescription.fragments.anyCard}
    ${Team.fragments.anyCard}
    ${InSeasonWarning.fragments.anyCard}
  ` as TypedDocumentNode<TokenSummary_anyCard>,
};
